import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "./Navigation.js";
import { getTokenInternal } from "./useToken";

export default function TrustedClaimers() {
  const [token, setToken] = useState();
  const [friends, setFriends] = useState([]);
  const [trustedIds, setTrustedIds] = useState([]);
  const [whoTrustsMe, setWhoTrustsMe] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const fetchedToken = await getTokenInternal();
      if (!fetchedToken) {
        navigate("/login");
        return;
      }
      setToken(fetchedToken);
    };
    init();
  }, []);

  const loadData = async (currentToken) => {
    const headers = { Authorization: `Bearer ${currentToken.access_token}` };

    const friendsRes = await fetch(
      `${process.env.REACT_APP_API_HOST}/api/friendsList`,
      { headers }
    );
    const friendsData = friendsRes.ok ? await friendsRes.json() : [];
    const normalized = friendsData.map((f) => ({
      friendAccountId:
        f.user_id !== currentToken.account.id ? f.user_id : f.friend_id,
      friend_username: f.friend_username,
    }));
    setFriends(normalized);

    const grantedRes = await fetch(
      `${process.env.REACT_APP_API_HOST}/api/trusted-claimers/granted-by-me`,
      { headers }
    );
    const grantedData = grantedRes.ok ? await grantedRes.json() : [];
    setTrustedIds(grantedData.map((t) => t.account_id));

    const trustsMeRes = await fetch(
      `${process.env.REACT_APP_API_HOST}/api/trusted-claimers/who-trusts-me`,
      { headers }
    );
    setWhoTrustsMe(trustsMeRes.ok ? await trustsMeRes.json() : []);
  };

  useEffect(() => {
    if (token) loadData(token);
  }, [token]);

  const handleToggle = async (friendAccountId, isTrusted) => {
    const headers = { Authorization: `Bearer ${token.access_token}` };
    if (isTrusted) {
      await fetch(
        `${process.env.REACT_APP_API_HOST}/api/trusted-claimers/revoke/${friendAccountId}`,
        { method: "DELETE", headers }
      );
    } else {
      await fetch(
        `${process.env.REACT_APP_API_HOST}/api/trusted-claimers/grant/${friendAccountId}`,
        { method: "POST", headers }
      );
    }
    await loadData(token);
  };

  if (!token) {
    return null;
  }

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto p-4 max-w-2xl">
        <h1 className="text-2xl font-bold mb-2">Trusted Claimers</h1>
        <p className="mb-6 text-gray-300">
          Trusted friends can mark items as found on your behalf while
          browsing your tracker. You still have to approve every claim
          before it counts.
        </p>

        <h2 className="text-lg font-bold mb-2">Friends I Trust</h2>
        {friends.length === 0 ? (
          <p className="mb-6">You don't have any friends yet.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-left mb-8">
            <thead>
              <tr>
                <th className="pr-4">Friend</th>
                <th>Trusted</th>
              </tr>
            </thead>
            <tbody>
              {friends.map((friend) => {
                const isTrusted = trustedIds.includes(friend.friendAccountId);
                return (
                  <tr key={friend.friendAccountId}>
                    <td className="pr-4 py-1">{friend.friend_username}</td>
                    <td className="py-1">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={isTrusted}
                          onChange={() =>
                            handleToggle(friend.friendAccountId, isTrusted)
                          }
                        />
                        <span className="slider"></span>
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <h2 className="text-lg font-bold mb-2">Who Trusts Me</h2>
        {whoTrustsMe.length === 0 ? (
          <p>No one has trusted you to submit claims for them yet.</p>
        ) : (
          <ul>
            {whoTrustsMe.map((account) => (
              <li key={account.account_id}>{account.username}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
